import { SiteMeta } from '@src/types/SiteMeta';
import storeConnector from '@store/storeConnector';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CommandsCategory } from '@src/types/Commands';
import { MqttClient } from 'mqtt';
import { memo, useMemo } from 'react';
import { CommandsModalContextProvider } from '../CommandsModalContext';
import useSubscription from '@src/hooks/useSubscription';
import { DataPoint, DataPointWithValue } from '@src/types/DataPoint';
import PowerSection from './PowerSection';
import useDataPointsSearch from '@src/hooks/api/queries/useDataPointsSearch';
import { generateDataPoint } from '@src/utils/generateTopic';
import useAlias from '@src/hooks/useAlias';

const StyledDialogTitle = styled(DialogTitle)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

interface Props {
  mqttClient: MqttClient;
  deviceId: string;
  deviceName: string;
  open: boolean;
  onClose: () => void;
  siteMeta: SiteMeta;
}

function PowinControllerCommandsModal({
  mqttClient,
  deviceId,
  deviceName,
  onClose,
  siteMeta,
}: Props) {
  const alias = useAlias();
  const onDialogClose = () => {
    onClose();
  };

  const MIN_P_POINT_NAME = siteMeta.PowinAllowedMinP_PointName;
  const MAX_P_POINT_NAME = siteMeta.PowinAllowedMaxP_PointName;
  const MIN_Q_POINT_NAME = siteMeta.PowinAllowedMinQ_PointName;
  const MAX_Q_POINT_NAME = siteMeta.PowinAllowedMaxQ_PointName;
  const QSET = 'Qset';
  const PSET = 'Pset';

  const dataPoints = useDataPointsSearch([
    { deviceType: 'site', pointName: PSET },
    { deviceType: 'site', pointName: QSET },
    { deviceType: 'site', pointName: MIN_P_POINT_NAME },
    { deviceType: 'site', pointName: MAX_P_POINT_NAME },
    { deviceType: 'site', pointName: MIN_Q_POINT_NAME },
    { deviceType: 'site', pointName: MAX_Q_POINT_NAME },
  ]);

  const pointsToSubscribe = useMemo(() => {
    const minMaxDataPoints: DataPoint[] = [
      MIN_P_POINT_NAME,
      MAX_P_POINT_NAME,
      MIN_Q_POINT_NAME,
      MAX_Q_POINT_NAME,
    ].map((pointName: string) =>
      generateDataPoint(
        {
          deviceType: 'tsc',
          controllerId: siteMeta.siteId,
          sourceDeviceId: deviceId,
          pointName,
          pointAlias: pointName,
        },
        siteMeta.siteId,
      ),
    );
    return [...dataPoints, ...minMaxDataPoints];
  }, [
    deviceId,
    MIN_P_POINT_NAME,
    MAX_P_POINT_NAME,
    MIN_Q_POINT_NAME,
    MAX_Q_POINT_NAME,
    siteMeta.siteId,
    dataPoints,
  ]);

  const dataPointsWithValue: DataPointWithValue[] = useSubscription(
    pointsToSubscribe,
    mqttClient,
  );

  return (
    <CommandsModalContextProvider
      category={CommandsCategory.POWINEMS}
      deviceId={siteMeta.siteId}
    >
      <Dialog open onClose={onDialogClose} maxWidth='sm' fullWidth>
        <StyledDialogTitle>
          {deviceName} {alias('Commands')}
          <IconButton onClick={onClose} sx={{ mr: -2, mt: -1 }}>
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent dividers>
          <PowerSection
            currentPset={dataPointsWithValue[0].value as string}
            currentQset={dataPointsWithValue[1].value as string}
            minPThreshold={Number(dataPointsWithValue[2].value)}
            maxPThreshold={Number(dataPointsWithValue[3].value)}
            minQThreshold={Number(dataPointsWithValue[4].value)}
            maxQThreshold={Number(dataPointsWithValue[5].value)}
          />
        </DialogContent>
      </Dialog>
    </CommandsModalContextProvider>
  );
}

export default storeConnector(memo(PowinControllerCommandsModal), {
  config: ['siteMeta'],
  user: ['role'],
});
